import { useMemo, type ReactNode } from 'react';

// The context module, not the feature's index: the index also exports the Account page, which reads
// the wallet, and importing it from here would make the two features import each other.
import { useAccountSession } from '@/features/account/context.js';
import { WalletContext } from './context.js';

/**
 * MYP-54's on-hold amount, which no real source has confirmed yet (see `WalletState`'s doc
 * comment). Kept as a named constant rather than
 * inlined in the provider so MYP-58 has one obvious place to replace each with the real thing
 * instead of hunting through `useMemo`.
 */
const FIXTURE_ON_HOLD_CENTS = 0;

/**
 * The wallet balance and the on-hold figure, app-wide (the header, the Dashboard and the pay
 * dialog all read the balance).
 *
 * The balance is only ever the vendor's figure from `alipay_page2` (MYP-105); after a payment
 * (MYP-92) the record is read again. Nothing here changes it locally any more — the demo top-up
 * that did is gone (MYP-106). Top-up history is not held here: it is a 553-row read only the
 * Wallet page needs, so that page loads it itself (`useTopUpHistory`, MYP-107).
 */
export function WalletProvider({ children }: { children: ReactNode }) {
  const { profile } = useAccountSession();

  /*
    Zero until the account loads, and for a record with no readable balance. Neither is shown as a
    figure: the header shows a dash until the session is ready and the page area is held back by
    `SessionGate`, so nothing displays this zero as a balance. The adapter reports "unreadable" as
    undefined precisely so it is not confused with a real zero, and this is the one place that has
    to pick.
  */
  /*
    Only ever the vendor's figure. The demo top-up that used to add to it in the browser is gone
    (MYP-106): a balance that changes without money moving is worse than one that waits for the
    real top-up.
  */
  const balanceCents = profile?.walletBalanceCents ?? 0;

  const value = useMemo(
    () => ({
      balanceCents,
      // The neutral default the formatters use, for the moment before the account has loaded.
      currency: profile?.walletCurrency ?? 'MYR',
      onHoldCents: FIXTURE_ON_HOLD_CENTS,
    }),
    [balanceCents, profile?.walletCurrency],
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
